<template>
    <v-dialog
        v-model="showDialog"
        max-width="500"
    >
        <v-card v-if="booking">

            <v-card-title class="align-start flex-column"
                          :style="{'background-color': $root.siteconfig.appbar.props.backgroundColor, color: $root.siteconfig.appbar.props.color }">
                <div class="fill-width font-weight-bold ml-3">
                    <span>{{serviceName}}</span>
                    <v-btn icon @click="showDialog = false" class="float-right mr-2"><v-icon :style="{'background-color': $root.siteconfig.appbar.props.backgroundColor, color: $root.siteconfig.appbar.props.color }">mdi-close</v-icon></v-btn>
                </div>
            </v-card-title>

            <v-card-text>
                <div class="zoezi-checkout-summary-resource-booking ma-2">
                    <div class="zoezi-checkout-summary-resource-booking-resource d-flex">
                        <div class="justify-end">
                            <v-avatar size="60">
                                <!--_FIXME: zoezi-user-avatar  -->
                                <v-auto-img class="zoezi-staff-image" :src="staffimagekey" />
                            </v-avatar>
                        </div>
                        <span class="ml-3 d-flex flex-column align-start justify-center">
                            <div class="text-uppercase">Utövare</div>
                            <div class="zoezi-resource-name font-weight-bold">{{resourceNames}}</div>
                        </span>
                    </div>

                    <div class="zoezi-checkout-summary-resource-booking-details pt-4" style="margin-left: 72px">
                        <div class="mb-2"><v-icon small color="black" class="mr-3">mdi-calendar-month-outline</v-icon>{{Date.newFull(bookingTime).hhmm()}}, {{Date.formatFullShort(bookingTime)}}</div>
                        <div class="mb-2"><v-icon small color="black" class="mr-3">mdi-clock-outline</v-icon>{{bookingDuration}} min</div>
                        <div class="mb-2" v-if="showPrice"><v-icon small color="black" class="mr-3">mdi-ticket-outline</v-icon>{{bookingPrice | price}}</div>

                        <template v-if="allResources.length > 1">
                            <div v-for="resource in allResources" :key="resource.id" class="mb-2">
                                <v-avatar class="mr-2" :size="32">
                                    <v-auto-img :src="$api.getImageUrl(resource.imagekey, 100)" />
                                </v-avatar>
                                {{resource.lastname}}
                            </div>
                        </template>

                        <div v-for="resource in sortedOptionalResources" :key="resource.id" class="mb-2">
                            <v-avatar class="mr-2" :size="32">
                                <v-auto-img :src="resource.imagekey" />
                            </v-avatar>
                            {{resource.lastname}}
                        </div>

                        <div v-if="contactInfo" class="d-flex"><v-icon small color="black" class="align-start mr-3 mt-1">mdi-map-marker-outline</v-icon>
                            <span>
                                <div>{{contactInfo.address}}</div>
                                <div>{{contactInfo.zipCode}} {{contactInfo.city}}</div>
                            </span>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-btn block @click="cancelBooking()" text color="error" :loading="loading" v-if="resourceBooking.unbookable">{{$translate('CANCEL_BOOKING')}}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'zoezi-resourcebooking-dialog',
    props: ['value', 'booking'],

    watch: {
        value: {
            immediate: true,
            handler(value) {
                this.showDialog = value;
            }
        },
        showDialog(value) {
            this.$emit('input', value);
        }
    },
    data: () => ({
        showDialog : false,
        loading: false
    }),

    computed: {
        // Normalize booking structure - data can exist directly on booking or nested in booking.rb
        resourceBooking() {
            return this.booking.rb || this.booking;
        },
        serviceName() {
            return this.booking.text || this.resourceBooking.servicename;
        },
        resourceNames() {
            // Show all resources from siblings if available
            if (this.booking.siblings && this.booking.siblings.length > 1) {
                const uniqueNames = [...new Set(this.booking.siblings.map(rb => rb.staffname).filter(Boolean))];
                return uniqueNames.join(', ');
            }
            return this.resourceBooking.staffname;
        },
        allResources() {
            // Get all resources from siblings for display
            if (this.booking.siblings && this.booking.siblings.length > 1) {
                const resources = [];
                this.booking.siblings.forEach(rb => {
                    if (rb.staffname) {
                        resources.push({
                            id: rb.staff_id,
                            lastname: rb.staffname,
                            imagekey: rb.staff_imagekey
                        });
                    }
                });
                // Remove duplicates by id
                const uniqueResources = resources.filter((resource, index, self) => 
                    index === self.findIndex(r => r.id === resource.id)
                );
                return uniqueResources;
            }
            return [];
        },
        staffimagekey() {
            return this.resourceBooking.staff_imagekey;
        },
        bookingTime() {
            return this.resourceBooking.time;
        },
        bookingDuration() {
            return this.resourceBooking.duration;
        },
        bookingPrice() {
            return this.resourceBooking.price;
        },
        contactInfo() { // Priority: Resource with address => extraResource/locationResource => Site Address => Visit address => address
            if (this.resourceBooking.resource_location?.address) {
                return this.resourceBooking.resource_location;
            }

            if (this.resourceBooking.extra_resource_location?.address) {
                return this.resourceBooking.extra_resource_location;
            }

            if (this.resourceBooking.optional_resources?.length >= 1 && this.resourceBooking.optional_resources[0].address) {
                return this.resourceBooking.optional_resources[0];
            }

            if (this.$store.state.settings?.sites?.length > 1) { // MultiSite
                const site = this.$store.state.settings.sites.find(s => s.id == this.resourceBooking.site_id);
                return site;
            }

            const location = this.$store.state.settings.contactinfo;
            if (location.visit_address && location.visit_city && location.visit_zipCode) {
                return { address: location.visit_address, city: location.visit_city, zipCode: location.visit_zipCode };
            }

            return location;
        },
        showPrice() {
            return !!this.$store.state.settings?.modules?.Payment;
        },
        sortedOptionalResources() {
            const optionalResources = this.resourceBooking.optional_resources || [];

            // Sort optional resources on name
            return optionalResources.sort((a, b) => a.lastname.localeCompare(b.lastname));
        },
    },

    methods: {
        cancelBooking() {
            this.loading = true;
            this.$api.post('/api/memberapi/resourcebooking/cancel', null, {id: this.resourceBooking.id, sameDateOnly: true}).then((response) => {
                this.loading = false;

                if (!response.ok) {
                    this.$store.commit('showErrorDialog', {title: this.$translate('Cancellation error'), text: this.$translate('The booking could not be cancelled')});
                }

                this.showDialog = !response.ok;
            });
        },
        // FIXME: Move to central place
        formatTime(time) {
            let date = Date.newFull(time);
            var ft = this.$store.state.now30s.clone().clearTime().daysUntil(date);
            if (ft > 6 || ft < 0 || date.getWeekNumber() > new Date().getWeekNumber()) {
                return date.yyyymmdd();
            } else {
                let res = [];
                if (ft == 0) {
                    res = ['Today'];
                } else if (ft == 1) {
                    res = ['Tomorrow', date.getWeekDayString()];
                } else {
                    res = [date.getWeekDayString()];
                }

                res.push(date.hhmm());

                return res.map(this.$translate).join(' ');
            }
        }
    }
}
</script>
